import _ from 'lodash';

import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Divider,
    InputLabel,
    MenuItem,
    Select,
    TextField,
    Typography
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import React, { useCallback, useMemo } from 'react';
import { CheckCircleOutlined } from '@material-ui/icons';
import {
    CardContentStyled,
    CardStyled,
    CollapsibleCard
} from '../../../../../components/common/collapsible/CollapsibleCard';

const AdvancedOptionsDiv = styled.div`
    margin-top: 24px;
`;

const AttributeNameInput = styled(TextField)`
    width: 400px;
`;

const FieldLabelDiv = styled.div`
    margin-top: 12px;
`;

const StyledSelect = styled(Select)`
    width: 400px;
`;

const SmallSelect = styled(Select)`
    width: 80px;
`;

const SmallTD = styled.td`
    width: 80px;
`;

const SmallTable = styled.table`
    width: 200px;
`;

const SelectDiv = styled.div`
    display: flex;
    flex-direction: column;
`;

const ExtendedDivider = styled(Divider)`
    margin-top: 12px;
    margin-bottom: 12px;
`;

enum SyncType {
    AllClasses = 'all-classes',
    SomeClasses = 'some-classes'
}

enum ContentLearningTools {
    LmsContent = 'lms-content',
    InstructorChoice = 'instructor-choice'
}

const currentUserTimezone = new Date().getTimezoneOffset() * 60 * 1000;

const LMSOptions = ({ integrationType, lmsBaseUrl, lmsKey, lmsSecret, syncSettings, handleChange }) => {
    const { t } = useTranslation('admin_settings');

    const {
        enable_roster: enableRoster,
        enable_attendance: enableAttendance,
        enable_content: enableContent,
        sync_type: syncType,
        sync_course_ids: syncCourseIds,
        content_learning_tools: contentLmsTools,
        sync_count: syncCount = 0,
        sync_times: syncTimes
    } = syncSettings || {};

    const clonedSyncTimes = useMemo(() => {
        let syncTimesTemp = _.cloneDeep(syncTimes);

        if (!syncTimesTemp) {
            syncTimesTemp = {};
        }

        for (let syncContentIndex = 0; syncContentIndex <= 5; syncContentIndex++) {
            if (syncContentIndex >= syncCount) {
                // delete removed times if user decreased sync_count
                delete syncTimesTemp[String(syncContentIndex)];
            } else if (!syncTimesTemp[String(syncContentIndex)]) {
                // add default for newly added time
                syncTimesTemp[String(syncContentIndex)] = {
                    hours: 12,
                    minutes: 0,
                    ampm: 'AM',
                    timezone_offset: currentUserTimezone
                };
            } else if (syncTimesTemp[String(syncContentIndex)].timezone_offset !== currentUserTimezone) {
                // adjust timezone offset based on current user timezone
                const { hours, minutes, ampm, timezone_offset: originalOffset } = syncTimesTemp[String(syncContentIndex)];

                const originalDate = new Date();
                originalDate.setHours(ampm === 'AM' ? hours : hours + 12);
                originalDate.setMinutes(minutes);

                // calculate the UTC time out of the original time offset
                const utcTime = new Date(originalDate.getTime() + originalOffset);
                // create a new Date representing the original time in the current user timezone
                const userDate = new Date(utcTime.getTime() - currentUserTimezone);

                syncTimesTemp[String(syncContentIndex)] = {
                    hours: userDate.getHours() % 12 || 12,
                    minutes: userDate.getMinutes(),
                    ampm: userDate.getHours() > 12 ? 'PM' : 'AM',
                    timezone_offset: currentUserTimezone
                };
            }
        }
        return syncTimesTemp;
    }, [syncCount, syncTimes]);

    const handleSyncSettingsChange = useCallback((event) => {
        const { name, value, checked } = event.target;
        handleChange({
            target: {
                name: 'sync_settings',
                value: {
                    ...syncSettings,
                    [name]: ['enable_roster', 'enable_attendance', 'enable_content'].includes(name) ? checked : value
                }
            }
        });
    }, [handleChange, syncSettings]);

    const handleSyncTimeChange = useCallback((event) => {
        const { name, value } = event.target;
        const [syncTimesIndex, syncTimesAttribute] = name.split('_');
        clonedSyncTimes[syncTimesIndex][syncTimesAttribute] = value;
        clonedSyncTimes[syncTimesIndex].timezone_offset = currentUserTimezone;
        handleSyncSettingsChange({
            target: {
                name: 'sync_times',
                value: clonedSyncTimes
            }
        });
    }, [clonedSyncTimes, handleSyncSettingsChange]);

    const hasConnectionConfigFilled = Boolean(lmsBaseUrl) && Boolean(lmsKey) && Boolean(lmsSecret);
    const hasAnyFeatureEnabled = enableContent || enableAttendance || enableRoster;
    const hasCroneJobFeatureEnabled = enableContent || (enableRoster);

    const cardLmsConnectionConfig = (
        <CollapsibleCard
            title={t('lms-sync-connection-config')}
            titleIcon={hasConnectionConfigFilled ? <CheckCircleOutlined /> : undefined}
            id="lms-sync-connection-config"
            defaultExpanded={!hasConnectionConfigFilled}
            rows={[
                {
                    key: 'lms_base_url',
                    element: (
                        <FieldLabelDiv>
                            <InputLabel>{t('lms-base-url-name')}</InputLabel>
                            <AttributeNameInput size="small" name="lms_base_url" value={lmsBaseUrl} onChange={handleChange} />
                        </FieldLabelDiv>
                    )
                },
                {
                    key: 'lms_key',
                    element: (
                        <FieldLabelDiv>
                            <InputLabel>{t('lms-key-name')}</InputLabel>
                            <AttributeNameInput size="small" name="lms_key" value={lmsKey} onChange={handleChange} />
                        </FieldLabelDiv>
                    )
                },
                {
                    key: 'lms_secret',
                    element: (
                        <FieldLabelDiv>
                            <InputLabel> {t('lms-secret-name')}</InputLabel>
                            <AttributeNameInput size="small" name="lms_secret" value={lmsSecret} onChange={handleChange} />
                        </FieldLabelDiv>
                    )
                }
            ]}
        />
    );

    const cardCommonSettings = (
        <CardStyled>
            <CardContentStyled>
                <SelectDiv>
                    <InputLabel id="lms-select-sync-type-label" disabled={!hasAnyFeatureEnabled}>
                        {t('lms-select-sync-type')}
                    </InputLabel>
                    <StyledSelect
                        name="sync_type"
                        value={syncType}
                        label={t('lms-select-sync-type')}
                        labelId="select-sync-type"
                        onChange={handleSyncSettingsChange}
                        size="small"
                        disabled={!hasAnyFeatureEnabled}
                    >
                        {Object.values(SyncType).map((contentSyncType) => (
                            <MenuItem key={`lms-sync-type-${contentSyncType}`} value={contentSyncType}>
                                {t(contentSyncType)}
                            </MenuItem>
                        ))}
                    </StyledSelect>
                </SelectDiv>
                {syncType === SyncType.SomeClasses && (
                    <FieldLabelDiv>
                        <InputLabel id="lms-select-sync-course-ids-label" disabled={!hasAnyFeatureEnabled}>
                            {t('lms-sync-course-ids')}
                        </InputLabel>
                        <AttributeNameInput
                            size="small"
                            name="sync_course_ids"
                            value={syncCourseIds}
                            onChange={handleSyncSettingsChange}
                            disabled={!hasAnyFeatureEnabled}
                        />
                    </FieldLabelDiv>
                )}
                <ExtendedDivider />
                <SelectDiv>
                    <InputLabel id="lms-select-sync-count-label" disabled={!hasCroneJobFeatureEnabled}>
                        {t('lms-sync-count')}
                    </InputLabel>
                    <StyledSelect
                        name="sync_count"
                        value={syncCount}
                        label={t('lms-sync-count')}
                        labelId="select-lms-sync-count"
                        onChange={handleSyncSettingsChange}
                        size="small"
                        disabled={!hasCroneJobFeatureEnabled}
                    >
                        {[...Array(5).keys()].map((i) => (
                            <MenuItem key={`lms-sync-count-${i}`} value={i}>
                                {i}
                            </MenuItem>
                        ))}
                    </StyledSelect>
                </SelectDiv>
                {_.values(clonedSyncTimes).map((clonedSyncTime, i) => (
                    <SelectDiv key={i}>
                        <InputLabel id={`lms-sync-time-${i}`} disabled={!hasCroneJobFeatureEnabled}>
                            {t(`sync-time-${i}`)}
                        </InputLabel>
                        <SmallTable>
                            <tbody>
                                <tr>
                                    <SmallTD>
                                        <SmallSelect
                                            name={`${i}_hours`}
                                            value={clonedSyncTime.hours}
                                            onChange={handleSyncTimeChange}
                                            size="small"
                                            disabled={!hasCroneJobFeatureEnabled}
                                        >
                                            {[...Array(12).keys()].map((itemIndex) => (
                                                <MenuItem key={`lms-sync-time-1-${itemIndex + 1}`} value={itemIndex + 1}>
                                                    {itemIndex + 1}
                                                </MenuItem>
                                            ))}
                                        </SmallSelect>
                                    </SmallTD>
                                    <SmallTD>:</SmallTD>
                                    <SmallTD>
                                        <SmallSelect
                                            name={`${i}_minutes`}
                                            value={clonedSyncTime.minutes}
                                            onChange={handleSyncTimeChange}
                                            size="small"
                                            disabled={!hasCroneJobFeatureEnabled}
                                        >
                                            {['00', '15', '30', '45'].map((minutesStr) => (
                                                <MenuItem key={`lms-sync-minutes-0-${minutesStr}`} value={Number(minutesStr)}>
                                                    {minutesStr}
                                                </MenuItem>
                                            ))}
                                        </SmallSelect>
                                    </SmallTD>
                                    <SmallTD>
                                        <SmallSelect
                                            name={`${i}_ampm`}
                                            value={clonedSyncTime.ampm}
                                            onChange={handleSyncTimeChange}
                                            size="small"
                                            disabled={!hasCroneJobFeatureEnabled}
                                        >
                                            <MenuItem key={`lms-sync-minutes-0-${i}`} value={'AM'}>
                                                {'AM'}
                                            </MenuItem>
                                            <MenuItem key={`lms-sync-minutes-15-${i}`} value={'PM'}>
                                                {'PM'}
                                            </MenuItem>
                                        </SmallSelect>
                                    </SmallTD>
                                </tr>
                            </tbody>
                        </SmallTable>
                    </SelectDiv>
                ))}
            </CardContentStyled>
        </CardStyled>
    );
    return (
        <AdvancedOptionsDiv>
            <Accordion>
                <AccordionSummary expandIcon={<ExpandMoreIcon />} aria-controls="panel1a-content" id="panel1a-header">
                    <Typography>{t('sync-settings')}</Typography>
                </AccordionSummary>
                <AccordionDetails>
                    {cardLmsConnectionConfig}
                    {cardCommonSettings}

                    {integrationType === 'Blackboard' && (
                        <CollapsibleCard
                            title={t('lms-enable-attendance-sync')}
                            id="enable-attendance"
                            toggle={{
                                name: 'enable_attendance',
                                checkedState: enableAttendance,
                                onChange: handleSyncSettingsChange
                            }}
                            rows={[]}
                        />
                    )}

                    <CollapsibleCard
                        title={t('lms-enable-content-sync')}
                        id="enable-content"
                        toggle={{
                            name: 'enable_content',
                            checkedState: enableContent,
                            onChange: handleSyncSettingsChange
                        }}
                        rows={[
                            {
                                key: 'lms-content-learning-tools',
                                element: (
                                    <SelectDiv>
                                        <InputLabel id="select-content-learning-tools">{t('lms-select-learning-tools')}</InputLabel>
                                        <StyledSelect
                                            name="content_learning_tools"
                                            value={contentLmsTools || ContentLearningTools.LmsContent}
                                            label={t('lms-select-learning-tools')}
                                            labelId="lms-select-learning-tools"
                                            onChange={handleSyncSettingsChange}
                                            size="small"
                                        >
                                            {Object.values(ContentLearningTools).map((learningTool) => (
                                                <MenuItem key={`lms-select-learning-tool-${learningTool}`} value={learningTool}>
                                                    {t(`lms-learning-tool-${learningTool}`)}
                                                </MenuItem>
                                            ))}
                                        </StyledSelect>
                                    </SelectDiv>
                                )
                            }
                        ]}
                    />

                    <CollapsibleCard
                        title={t('lms-enable-roster-sync')}
                        id="enable-roster"
                        toggle={{
                            name: 'enable_roster',
                            checkedState: enableRoster,
                            onChange: handleSyncSettingsChange
                        }}
                        rows={[]}
                    />
                </AccordionDetails>
            </Accordion>
        </AdvancedOptionsDiv>
    );
};
export default LMSOptions;
